import {
  type FC,
  type SyntheticEvent,
  type FocusEvent,
  useCallback,
  useRef,
  useState,
  useEffect
} from 'react';
import classnames from 'classnames';

import Icon from 'app/components/Icon';
import arrowIcon from 'app/icons/arrow.svg';
import { useEnterPress } from 'app/hooks/useEnterPress';
import type { InputProps } from 'app/components/Input/index';
import Input from 'app/components/Input/index';

import style from './style.scss';
import {
  preventKeypress,
  handleChange,
  isDecrementable,
  isIncrementable,
  handleKeyDown,
  incrementValue
} from './utils';

interface Props extends Omit<InputProps, 'type' | 'onChange'> {
  onChange?: (value: string, event?: SyntheticEvent<HTMLInputElement> | WheelEvent) => void;
  incrementBy?: number;
  withArrows?: boolean;
  customClassName?: (value: string) => string | undefined;
  minValue?: number;
  maxValue?: number;
  loseFocusOnEnter?: boolean;
  error?: boolean;
  containerClassName?: string;
  resizeToContent?: boolean;
  withMouseWheelIncrement?: boolean;
  acceptFloat?: boolean;
}

const RawInputNumber: FC<Omit<Props, 'status'>> = ({
  incrementBy = 0.01,
  onChange,
  minValue,
  maxValue,
  acceptFloat = true,
  ...props
}) => {
  const incrementStep = acceptFloat ? incrementBy : Math.max(Math.floor(incrementBy), 1);

  return (
    <input
      type="text"
      onChange={onChange && handleChange(onChange, minValue, maxValue, acceptFloat)}
      onKeyPress={preventKeypress}
      onKeyDown={onChange && handleKeyDown(onChange, incrementStep, minValue, maxValue)}
      {...props}
    />
  );
};

const InputNumber: FC<Props> = ({
  incrementBy = 0.01,
  withArrows,
  minValue,
  maxValue,
  className,
  disabled,
  onChange,
  loseFocusOnEnter = false,
  error,
  containerClassName,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  withMouseWheelIncrement = false,
  value,
  acceptFloat = true,
  resizeToContent: _resizeToContent,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  const incrementStep = acceptFloat ? incrementBy : Math.max(Math.floor(incrementBy), 1);

  const increment =
    withArrows && onChange && !disabled
      ? () => {
          const newValue = incrementValue(
            value?.toString() ?? '',
            incrementStep,
            undefined,
            maxValue
          );
          onChange(newValue.toString());
        }
      : undefined;
  const decrement =
    withArrows && onChange && !disabled
      ? () => {
          const newValue = incrementValue(
            value?.toString() ?? '',
            -incrementStep,
            minValue,
            undefined
          );
          onChange(newValue.toString());
        }
      : undefined;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(
    document.querySelector('[class*="mainContainer "')
  );

  const handleBlur = () => inputRef && inputRef.current && inputRef.current.blur();

  useEnterPress(loseFocusOnEnter, handleBlur);

  const onWheel = useCallback(
    (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (e.deltaY < 0) {
        const newValue = incrementValue(
          value?.toString() ?? '',
          incrementStep,
          undefined,
          maxValue
        );
        onChange?.(newValue.toString(), e);
      } else {
        const newValue = incrementValue(
          value?.toString() ?? '',
          -incrementStep,
          minValue,
          undefined
        );
        onChange?.(newValue.toString(), e);
      }
    },
    [onChange, maxValue, minValue, incrementStep]
  );

  useEffect(() => {
    if (disabled) {
      onBlur();
    }
  }, [disabled]);

  useEffect(() => {
    const input = inputRef.current;
    const container = containerRef.current;

    if (input && container && focused && withMouseWheelIncrement && !disabled) {
      container.addEventListener('wheel', onWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', onWheel);
      }
    };
  }, [inputRef, withMouseWheelIncrement, onWheel, focused, disabled]);

  return (
    <span className={classnames(style.container, containerClassName)}>
      <Input
        ref={inputRef}
        type="text"
        className={classnames(className, { [style.input_withArrows]: withArrows })}
        disabled={disabled}
        onChange={onChange && handleChange(onChange, minValue, maxValue, acceptFloat)}
        onKeyPress={preventKeypress}
        onKeyDown={onChange && handleKeyDown(onChange, incrementStep, minValue, maxValue)}
        error={error}
        onBlur={e => {
          onBlur();
          onBlurProp?.(e);
        }}
        onFocus={e => {
          onFocus();
          onFocusProp?.(e);
        }}
        value={value}
        {...props}
      />
      {withArrows && (
        <span className={style.arrows}>
          {isIncrementable(value?.toString() ?? '', maxValue) && (
            <div
              className={classnames(style.arrowButton, { [style.arrowButton_disabled]: disabled })}
              onClick={increment}
            >
              <Icon href={arrowIcon} className={style.arrowIcon} />
            </div>
          )}
          {isDecrementable(value?.toString() ?? '', minValue) && (
            <div
              className={classnames(style.arrowButton, { [style.arrowButton_disabled]: disabled })}
              onClick={decrement}
            >
              <Icon
                href={arrowIcon}
                className={classnames(style.arrowIcon, style.arrowIcon_decrement)}
              />
            </div>
          )}
        </span>
      )}
    </span>
  );
};

InputNumber.displayName = 'InputNumber';

const StatefullInputNumber: FC<Props> = ({
  customClassName,
  className,
  onChange,
  value,
  onFocus,
  onBlur,
  resizeToContent = false,
  ...props
}) => {
  const [localValue, setLocalValue] = useState(value !== undefined ? `${value}` : '');
  // use ref instead of state because we don't need rerender and avoid run useEffect to update local value on blur
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) {
      setLocalValue(value !== undefined ? `${value}` : '');
    }
  }, [value]);

  const classNames = customClassName ? [className, customClassName(localValue)] : className;
  const handleChange = (value: string, event?: SyntheticEvent<HTMLInputElement> | WheelEvent) => {
    setLocalValue(value);
    if (!event && onChange) {
      // if event isn't defined, it mean update comes from arrow buttons
      onChange(value);
    }
  };
  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setLocalValue(value !== undefined ? `${value}` : '');
    focusedRef.current = true;
    if (onFocus) {
      onFocus(event);
    }
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    focusedRef.current = false;
    const valueChanged = value !== localValue;
    if (valueChanged && onChange) {
      onChange(localValue, event);
    }
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <InputNumber
      {...props}
      className={classnames(classNames)}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      resizeToContent={resizeToContent}
      style={resizeToContent ? { width: `${localValue.length || 1}ch` } : {}}
    />
  );
};

StatefullInputNumber.displayName = 'StatefullInputNumber';

export default InputNumber;
export type { Props as InputNumberProps };
export { RawInputNumber, StatefullInputNumber };
